import { useRef, useState } from "react";
import { Paperclip, X } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";
import { Input } from "@monorepo-apps/ui/src/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import { useCreateFeatureRequest } from "@/application/use-cases/feature-requests/use-feature-requests.queries";
import { useUploadAttachment } from "@/application/use-cases/feature-requests/use-feature-request-thread.queries";
import {
  REQUEST_DESCRIPTION_TEMPLATES,
  REQUEST_TYPES,
} from "@/presentation/components/requests/feature-request-meta";
import { useAuth } from "@/presentation/providers/auth.provider";
import type { FeatureRequestType } from "@/infrastructure/supabase/database.types";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB, matches the bucket limit

export function NewRequestDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { session } = useAuth();
  const create = useCreateFeatureRequest();
  const upload = useUploadAttachment();
  const fileInput = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [type, setType] = useState<FeatureRequestType>("feature");
  const [description, setDescription] = useState(REQUEST_DESCRIPTION_TEMPLATES.feature);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const reset = () => {
    setTitle("");
    setType("feature");
    setDescription(REQUEST_DESCRIPTION_TEMPLATES.feature);
    setFiles([]);
    setBusy(false);
    setFileError(null);
    create.reset();
  };
  const handleClose = () => {
    reset();
    onClose();
  };

  // Swap the description scaffold when the type changes — but only if the author
  // hasn't started filling it in (still empty or still the previous template).
  const changeType = (next: FeatureRequestType) => {
    setDescription((cur) =>
      cur.trim() === "" || cur === REQUEST_DESCRIPTION_TEMPLATES[type]
        ? REQUEST_DESCRIPTION_TEMPLATES[next]
        : cur
    );
    setType(next);
  };

  const stageFiles = (list: FileList | null) => {
    if (!list) return;
    setFileError(null);
    const next: File[] = [];
    for (const f of Array.from(list)) {
      if (f.size > MAX_UPLOAD_BYTES) setFileError(`"${f.name}" is over 10 MB.`);
      else next.push(f);
    }
    if (next.length) setFiles((prev) => [...prev, ...next]);
    if (fileInput.current) fileInput.current.value = "";
  };

  const canSubmit = !!title.trim() && !!session?.user?.id && !busy;
  const save = async () => {
    if (!canSubmit || !session?.user?.id) return;
    const userId = session.user.id;
    // If the description is still the untouched scaffold, store nothing.
    const descTrim = description.trim();
    const descToSave =
      descTrim === "" || description === REQUEST_DESCRIPTION_TEMPLATES[type] ? null : descTrim;
    setBusy(true);
    try {
      const row = await create.mutateAsync({
        title: title.trim(),
        description: descToSave,
        type,
        submitted_by: userId,
        submitter_email: session.user.email ?? null,
      });
      for (const file of files) {
        try {
          await upload.mutateAsync({ requestId: row.id, file, uploadedBy: userId });
        } catch {
          /* one bad upload shouldn't block the rest; drawer shows what landed */
        }
      }
      handleClose();
      onCreated(row.id); // open the drawer to continue the thread / see attachments
    } catch {
      /* create.isError renders below */
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="!max-w-xl">
        <DialogHeader>
          <DialogTitle>New feedback</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground -mt-1 text-[11.5px] leading-relaxed">
          A short, structured note helps us act faster. Use the prompts below — say what, where, and
          expected vs actual — and attach a screenshot if it helps point us to the spot.
        </p>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <div className="grid grid-cols-[1fr_9rem] gap-3">
            <div className="space-y-1.5">
              <label htmlFor="fr-title" className="text-foreground text-xs font-medium">
                Title<span className="text-destructive ml-0.5">*</span>
              </label>
              <Input
                id="fr-title"
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="One-line summary"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="fr-type" className="text-foreground text-xs font-medium">
                Type
              </label>
              <Select value={type} onValueChange={(v) => changeType(v as FeatureRequestType)}>
                <SelectTrigger id="fr-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REQUEST_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="fr-desc" className="text-foreground text-xs font-medium">
              Details
            </label>
            <Textarea
              id="fr-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={9}
              className="text-[12px]"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-foreground text-xs font-medium">Attachments</label>
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                className="text-primary flex items-center gap-1 text-[11px] font-semibold"
              >
                <Paperclip className="size-3" /> Add files
              </button>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => stageFiles(e.target.files)}
              />
            </div>
            {files.length > 0 ? (
              <ul className="flex flex-col gap-1">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="bg-muted/40 flex items-center justify-between gap-2 rounded px-2 py-1 text-[11px]"
                  >
                    <span className="truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      aria-label={`Remove ${f.name}`}
                      className="text-muted-foreground hover:text-destructive shrink-0"
                    >
                      <X className="size-3" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-[10.5px] italic">
                Screenshots, PDFs, docs, or short video (≤ 10 MB each).
              </p>
            )}
            {fileError ? <p className="text-destructive text-[10.5px]">{fileError}</p> : null}
          </div>
          {create.isError ? (
            <p className="text-destructive text-[11px]">Could not submit — please try again.</p>
          ) : null}
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void save()} disabled={!canSubmit}>
            {busy ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
